/**
 * User Conversations Modal — צפייה בשיחות לקוח
 * Admin view: list all conversations for a user, click to see full messages
 */

import { useState, useEffect, useRef } from 'react'
import { X, MessageSquare, ChevronRight, ChevronDown, Clock, User, Bot, Star, Send, BookOpen } from 'lucide-react'
import api from '../../services/api'
import { useDialogA11y } from '../../hooks/useDialogA11y'

interface Message {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
}

interface Conversation {
  _id: string
  title: string
  status: string
  channel?: string
  messageCount: number
  userRiskScore: number
  userRiskProfile: string
  startedAt: string
  lastMessageAt: string
  totalTokensUsed: number
  messages?: Message[]
}

interface Props {
  userId: string
  userName: string
  onClose: () => void
}

export default function UserConversationsModal({ userId, userName, onClose }: Props) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [messagesCache, setMessagesCache] = useState<Record<string, Message[]>>({})
  const [feedbackCache, setFeedbackCache] = useState<Record<string, any>>({})
  const [feedbackNotes, setFeedbackNotes] = useState('')
  const [feedbackRating, setFeedbackRating] = useState<'good' | 'needs_improvement'>('good')
  const [showFeedbackForm, setShowFeedbackForm] = useState<string | null>(null)
  const [savingFeedback, setSavingFeedback] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(true, dialogRef, onClose)

  useEffect(() => {
    fetchConversations()
  }, [userId])

  const fetchConversations = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/admin/users/${userId}/conversations`)
      setConversations(res.data?.conversations || [])
    } catch {
      setConversations([])
    } finally {
      setLoading(false)
    }
  }

  const loadFeedback = async (convId: string) => {
    if (feedbackCache[convId] !== undefined) return
    try {
      const res = await api.get(`/admin/conversation-feedback/${convId}`)
      setFeedbackCache(prev => ({ ...prev, [convId]: res.data?.feedback || null }))
    } catch {
      setFeedbackCache(prev => ({ ...prev, [convId]: null }))
    }
  }

  const handleSaveFeedback = async (convId: string) => {
    setSavingFeedback(true)
    try {
      await api.post('/admin/conversation-feedback', {
        conversationId: convId,
        clientUserId: userId,
        rating: feedbackRating,
        generalNotes: feedbackNotes,
      })
      setFeedbackCache(prev => ({
        ...prev,
        [convId]: { rating: feedbackRating, generalNotes: feedbackNotes },
      }))
      setShowFeedbackForm(null)
      setFeedbackNotes('')
    } catch (err) {
      console.error('Failed to save feedback:', err)
    } finally {
      setSavingFeedback(false)
    }
  }

  const handleConvertToRule = async (feedback: any) => {
    if (!feedback?.generalNotes) return
    try {
      await api.post('/admin/advisor-rules', {
        category: 'general',
        title: feedback.generalNotes.substring(0, 80),
        content: feedback.generalNotes,
        priority: feedback.rating === 'needs_improvement' ? 'mandatory' : 'recommended',
      })
      alert('הכלל נוצר בהצלחה! ניתן לערוך אותו בטאב "כללי יועצי"')
    } catch (err) {
      console.error('Failed to convert to rule:', err)
    }
  }

  const handleExpand = async (convId: string) => {
    if (expandedId === convId) {
      setExpandedId(null)
      return
    }

    setExpandedId(convId)
    loadFeedback(convId)

    // Load messages if not cached
    if (!messagesCache[convId]) {
      setLoadingMessages(true)
      try {
        const res = await api.get(`/admin/conversations/${convId}`)
        const msgs = res.data?.conversation?.messages || []
        setMessagesCache(prev => ({ ...prev, [convId]: msgs }))
      } catch {
        setMessagesCache(prev => ({ ...prev, [convId]: [] }))
      } finally {
        setLoadingMessages(false)
      }
    }
  }

  const formatDate = (d: string) => {
    const date = new Date(d)
    return date.toLocaleDateString('he-IL') + ' ' + date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <span className="badge badge-success text-xs">פעיל</span>
      case 'archived': return <span className="badge badge-gray text-xs">ארכיון</span>
      case 'flagged': return <span className="badge badge-error text-xs">מסומן</span>
      default: return <span className="badge text-xs">{status}</span>
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4" dir="rtl">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-conversations-modal-title"
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 id="user-conversations-modal-title" className="text-xl font-heading font-bold text-gray-900">
              שיחות — {userName}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {conversations.length} שיחות נמצאו
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="סגירה">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare size={40} className="mx-auto text-gray-300 mb-3" aria-hidden="true" />
              <p className="text-gray-500">אין שיחות למשתמש זה</p>
            </div>
          ) : (
            <div className="space-y-3">
              {conversations.map((conv) => {
                const isExpanded = expandedId === conv._id
                const messages = messagesCache[conv._id]

                return (
                  <div key={conv._id} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Conversation header — clickable */}
                    <button
                      onClick={() => handleExpand(conv._id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-right"
                    >
                      <div className="flex-shrink-0 text-gray-400">
                        {isExpanded ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
                      </div>
                      <MessageSquare size={18} className="flex-shrink-0 text-primary-500" aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conv.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                          <Clock size={12} aria-hidden="true" />
                          <span>{formatDate(conv.lastMessageAt || conv.startedAt)}</span>
                          <span className="text-gray-300">|</span>
                          <span>{conv.messageCount} הודעות</span>
                          {conv.totalTokensUsed > 0 && (
                            <>
                              <span className="text-gray-300">|</span>
                              <span>{conv.totalTokensUsed.toLocaleString()} tokens</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(conv.status)}
                      </div>
                    </button>

                    {/* Messages — expanded */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                        {loadingMessages && !messages ? (
                          <div className="flex justify-center py-6">
                            <div className="spinner w-6 h-6" />
                          </div>
                        ) : !messages || messages.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">אין הודעות</p>
                        ) : (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {messages.map((msg, i) => (
                              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {/* Avatar */}
                                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs ${
                                  msg.role === 'user'
                                    ? 'bg-primary-600 text-white'
                                    : msg.role === 'system'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-primary-100 text-primary-700'
                                }`}>
                                  {msg.role === 'user' ? <User size={14} aria-hidden="true" /> : <Bot size={14} aria-hidden="true" />}
                                </div>

                                {/* Bubble */}
                                <div className={`max-w-[85%] rounded-xl px-3 py-2 ${
                                  msg.role === 'user'
                                    ? 'bg-primary-600 text-white'
                                    : msg.role === 'system'
                                    ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                                    : 'bg-white border border-gray-200 text-gray-900'
                                }`}>
                                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                                    {msg.content}
                                  </p>
                                  <p className={`text-[10px] mt-1 ${
                                    msg.role === 'user' ? 'text-primary-200' : 'text-gray-400'
                                  }`}>
                                    {new Date(msg.timestamp).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Feedback section */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          {feedbackCache[conv._id] ? (
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <Star size={14} className={feedbackCache[conv._id].rating === 'good' ? 'text-green-500' : 'text-orange-500'} aria-hidden="true" />
                                  <span className="text-xs font-medium text-gray-700">
                                    {feedbackCache[conv._id].rating === 'good' ? 'שיחה תקינה' : 'דורש שיפור'}
                                  </span>
                                </div>
                                {feedbackCache[conv._id].generalNotes && (
                                  <button
                                    onClick={() => handleConvertToRule(feedbackCache[conv._id])}
                                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                                  >
                                    <BookOpen size={12} aria-hidden="true" />
                                    הפוך לכלל
                                  </button>
                                )}
                              </div>
                              {feedbackCache[conv._id].generalNotes && (
                                <p className="text-sm text-gray-600">{feedbackCache[conv._id].generalNotes}</p>
                              )}
                            </div>
                          ) : showFeedbackForm === conv._id ? (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setFeedbackRating('good')}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium border ${
                                    feedbackRating === 'good'
                                      ? 'bg-green-50 border-green-300 text-green-700'
                                      : 'bg-white border-gray-200 text-gray-500'
                                  }`}
                                >
                                  תקין
                                </button>
                                <button
                                  onClick={() => setFeedbackRating('needs_improvement')}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium border ${
                                    feedbackRating === 'needs_improvement'
                                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                                      : 'bg-white border-gray-200 text-gray-500'
                                  }`}
                                >
                                  דורש שיפור
                                </button>
                              </div>
                              <textarea
                                value={feedbackNotes}
                                onChange={e => setFeedbackNotes(e.target.value)}
                                placeholder="הערות..."
                                aria-label="הערות משוב"
                                className="input w-full text-sm"
                                rows={2}
                                style={{ fontSize: '16px' }}
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveFeedback(conv._id)}
                                  disabled={savingFeedback}
                                  className="btn btn-primary text-xs py-1.5"
                                >
                                  <Send size={12} aria-hidden="true" />
                                  שמור משוב
                                </button>
                                <button
                                  onClick={() => { setShowFeedbackForm(null); setFeedbackNotes('') }}
                                  className="btn btn-outline text-xs py-1.5"
                                >
                                  ביטול
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowFeedbackForm(conv._id)}
                              className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                            >
                              + הוסף משוב
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
